import { useEffect, useState } from "react";
import { OfficerLayout } from "@/components/OfficerLayout";
import { StatutoryRegister, StatutoryTabs } from "@/components/StatutoryRegister";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Search, Plus, X, MapPin } from "lucide-react";
import { toast } from "sonner";
import { formatINR } from "@/lib/i18n";

const empty = {
  panchayat_id: "", property_no: "", owner_name: "", owner_name_hi: "",
  mobile: "", family_id: "", aadhaar_last4: "", ward: "", address: "",
  property_type: "residential", built_area_sqft: "", annual_rental_value: "",
  gis_lat: "", gis_lng: "",
};

export default function Properties() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [panchayats, setPanchayats] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [regTab, setRegTab] = useState("module");

  const load = () => {
    const params = {};
    if (q) params.search = q;
    if (user?.panchayat_id) params.panchayat_id = user.panchayat_id;
    api.get("/properties", { params }).then((r) => setList(r.data));
  };

  useEffect(() => { load(); }, [q, user]);
  useEffect(() => {
    api.get("/panchayats").then((r) => {
      setPanchayats(r.data);
      setForm((f) => ({ ...f, panchayat_id: user?.panchayat_id || r.data[0]?.id || "" }));
    });
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      payload.built_area_sqft = parseFloat(payload.built_area_sqft) || 0;
      payload.annual_rental_value = parseFloat(payload.annual_rental_value) || 0;
      payload.gis_lat = payload.gis_lat ? parseFloat(payload.gis_lat) : null;
      payload.gis_lng = payload.gis_lng ? parseFloat(payload.gis_lng) : null;
      await api.post("/properties", payload);
      toast.success("Assessment created");
      setShow(false);
      setForm({ ...empty, panchayat_id: user?.panchayat_id || panchayats[0]?.id || "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <OfficerLayout>
      <div>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">{t("properties")}</div>
            <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900">{t("properties")}</h1>
            <p className="mt-1 text-sm text-slate-600">{lang === "hi" ? "संपत्तियों का आकलन एवं प्रबंधन।" : "Assess and manage panchayat properties."}</p>
          </div>
          {regTab === "module" && (
          <div className="flex gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="properties-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("search") + "…"}
                className="pl-9 pr-3 py-2 rounded-md border border-slate-300 bg-white text-sm w-64 outline-none focus:border-[#0A3161]"
              />
            </div>
            <button
              data-testid="new-assessment-btn"
              onClick={() => setShow(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md text-white"
              style={{ background: "#0A3161" }}
            >
              <Plus className="w-4 h-4" /> {t("newAssessment")}
            </button>
          </div>
          )}
        </div>

        <StatutoryTabs moduleLabel={lang === "hi" ? "आकलन" : "Assessments"} codes={["XIII-A", "XIII-B", "XIII-C", "XIV"]} active={regTab} onChange={setRegTab} />
        {regTab !== "module" && <StatutoryRegister code={regTab} />}

        <div className={regTab === "module" ? "" : "hidden"}>
        <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="properties-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              <tr>
                <th className="text-left px-4 py-3">{t("assessmentNo")}</th>
                <th className="text-left px-4 py-3">{t("propertyNo")}</th>
                <th className="text-left px-4 py-3">{t("owner")}</th>
                <th className="text-left px-4 py-3">{t("ward")}</th>
                <th className="text-left px-4 py-3">{t("propertyType")}</th>
                <th className="text-right px-4 py-3">{t("builtArea")}</th>
                <th className="text-right px-4 py-3">{t("annualRentalValue")}</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-xs">{p.assessment_no}</td>
                  <td className="px-4 py-3 font-mono text-xs">{p.property_no}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{p.owner_name}</div>
                    <div className="text-xs text-slate-500">{p.address}</div>
                  </td>
                  <td className="px-4 py-3">{p.ward}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider rounded font-semibold bg-slate-100 text-slate-700">
                      {t(p.property_type)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{p.built_area_sqft?.toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatINR(p.annual_rental_value)}</td>
                  <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                    <button data-testid={`prop-reassess-${p.id}`}
                      onClick={async () => {
                        const arv = prompt(`Reassess — new Annual Rental Value (current: ₹${p.annual_rental_value || 0}):`, p.annual_rental_value);
                        if (!arv) return;
                        const area = prompt(`New built-up area (sqft):`, p.built_area_sqft);
                        if (!area) return;
                        const reason = prompt("Reason for reassessment (optional):") || "Periodic revision";
                        try {
                          await api.post(`/properties/${p.id}/reassess`, {
                            annual_rental_value: parseFloat(arv),
                            built_area_sqft: parseFloat(area), reason });
                          toast.success("Reassessed"); load();
                        } catch (e) { toast.error("Failed"); }
                      }}
                      className="px-2 py-1 text-[10px] font-semibold rounded border border-[#0A3161] text-[#0A3161] hover:bg-[#0A3161]/5">
                      Reassess
                    </button>
                    <button data-testid={`prop-transfer-${p.id}`}
                      onClick={async () => {
                        const newOwner = prompt(`Transfer — new owner name (current: ${p.owner_name}):`);
                        if (!newOwner) return;
                        const mobile = prompt("New owner mobile:") || null;
                        const reason = prompt("Reason (sale / inheritance / gift / court_order / other):", "sale") || "sale";
                        const date = prompt("Transfer date (YYYY-MM-DD):", new Date().toISOString().slice(0, 10)) || new Date().toISOString().slice(0, 10);
                        try {
                          await api.post(`/properties/${p.id}/transfer`, {
                            new_owner_name: newOwner, new_owner_mobile: mobile,
                            transfer_reason: reason, transfer_date: date });
                          toast.success("Transferred"); load();
                        } catch (e) { toast.error("Failed"); }
                      }}
                      className="px-2 py-1 text-[10px] font-semibold rounded border border-[#E87722] text-[#E87722] hover:bg-[#E87722]/5">
                      Transfer
                    </button>
                    <button data-testid={`prop-liability-${p.id}`}
                      onClick={async () => {
                        const kind = prompt("Liability kind (mortgage / lien / burden / court_attachment):", "mortgage");
                        if (!kind) return;
                        const holder = prompt("Holder name (e.g., bank or party):");
                        if (!holder) return;
                        const amount = prompt("Amount (₹):", "0");
                        if (amount === null) return;
                        const doc = prompt("Document reference (optional):") || null;
                        try {
                          await api.post("/liabilities", {
                            property_id: p.id, kind, holder_name: holder,
                            amount: parseFloat(amount) || 0,
                            document_ref: doc,
                            created_on: new Date().toISOString().slice(0, 10),
                          });
                          toast.success("Liability recorded");
                        } catch (e) { toast.error("Failed"); }
                      }}
                      className="px-2 py-1 text-[10px] font-semibold rounded border border-amber-700 text-amber-800 hover:bg-amber-50">
                      Burden
                    </button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr><td colSpan={8} className="py-12 text-center text-slate-400 text-sm">No properties found</td></tr>
              )}
            </tbody>
          </table>
        </div>
        </div>

        {/* Modal */}
        {show && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="new-assessment-modal">
            <div className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-5 border-b border-slate-200">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#E87722] font-semibold">{t("newAssessment")}</div>
                  <h2 className="font-heading text-xl font-bold mt-1">New Property Assessment</h2>
                </div>
                <button onClick={() => setShow(false)} className="p-2 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={submit} className="p-5 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label={t("panchayat")} required>
                    <select required value={form.panchayat_id} onChange={(e) => setForm({ ...form, panchayat_id: e.target.value })} className="form-input" data-testid="form-panchayat">
                      {panchayats.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </Field>
                  <Field label={t("propertyNo")} required>
                    <input required value={form.property_no} onChange={(e) => setForm({ ...form, property_no: e.target.value })} className="form-input" data-testid="form-property-no" />
                  </Field>
                  <Field label={t("owner")} required>
                    <div className="flex gap-2">
                      <input required value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="form-input" data-testid="form-owner" />
                      <button type="button" data-testid="aadhaar-verify-btn"
                        onClick={async () => {
                          const last4 = prompt("Enter last 4 digits of Aadhaar (DEMO — try 1001, 1002, 1003, 1234):");
                          if (!last4) return;
                          try {
                            await api.post("/integrations/aadhaar/send-otp", {});
                            const otp = prompt("DEMO OTP sent. Use 123456 to verify:");
                            if (!otp) return;
                            const r = await api.post("/integrations/aadhaar/verify", { aadhaar_last4: last4, otp });
                            setForm({ ...form, owner_name: r.data.person.name, aadhaar_last4: last4 });
                            toast.success("Aadhaar verified (DEMO)");
                          } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                        }}
                        className="shrink-0 px-2.5 text-xs font-semibold rounded-md border border-[#0A3161] text-[#0A3161] hover:bg-[#0A3161]/5 whitespace-nowrap">
                        {t("aadhaarVerify")}
                      </button>
                    </div>
                  </Field>
                  <Field label={t("mobile")}>
                    <div className="flex gap-2">
                      <input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="form-input" />
                      <button type="button" data-testid="ppp-lookup-btn"
                        onClick={async () => {
                          const fid = prompt("Enter Family ID / PPP No. (DEMO — try HR-1234567890 or HR-9876543210):");
                          if (!fid) return;
                          try {
                            const r = await api.post("/integrations/ppp/lookup", { family_id: fid });
                            setForm({ ...form, family_id: fid, owner_name: form.owner_name || r.data.family.head_of_family });
                            toast.success(`PPP found: ${r.data.family.head_of_family} (DEMO)`);
                          } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                        }}
                        className="shrink-0 px-2.5 text-xs font-semibold rounded-md border border-[#0A3161] text-[#0A3161] hover:bg-[#0A3161]/5 whitespace-nowrap">
                        PPP
                      </button>
                    </div>
                  </Field>
                  <Field label={t("ward")} required>
                    <input required value={form.ward} onChange={(e) => setForm({ ...form, ward: e.target.value })} className="form-input" />
                  </Field>
                  <Field label={t("propertyType")}>
                    <select value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className="form-input">
                      <option value="residential">{t("residential")}</option>
                      <option value="commercial">{t("commercial")}</option>
                      <option value="industrial">{t("industrial")}</option>
                      <option value="mixed">{t("mixed")}</option>
                    </select>
                  </Field>
                  <Field label={t("builtArea")}>
                    <input type="number" value={form.built_area_sqft} onChange={(e) => setForm({ ...form, built_area_sqft: e.target.value })} className="form-input" />
                  </Field>
                  <Field label={t("annualRentalValue")}>
                    <input type="number" value={form.annual_rental_value} onChange={(e) => setForm({ ...form, annual_rental_value: e.target.value })} className="form-input" />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label={t("address")} required>
                      <input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="form-input" />
                    </Field>
                  </div>
                  <Field label="GIS Lat">
                    <input value={form.gis_lat} onChange={(e) => setForm({ ...form, gis_lat: e.target.value })} className="form-input" placeholder="28.89" />
                  </Field>
                  <Field label="GIS Lng">
                    <input value={form.gis_lng} onChange={(e) => setForm({ ...form, gis_lng: e.target.value })} className="form-input" placeholder="76.60" />
                  </Field>
                </div>
                <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                  <button type="button" onClick={() => setShow(false)} className="px-4 py-2 text-sm rounded-md border border-slate-300 text-slate-700">{t("cancel")}</button>
                  <button type="submit" disabled={busy} data-testid="form-submit" className="px-4 py-2 text-sm font-semibold rounded-md text-white" style={{ background: "#0A3161" }}>
                    {busy ? "…" : t("save")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .form-input {
          width: 100%;
          padding: 0.6rem 0.75rem;
          border-radius: 0.375rem;
          border: 1px solid #CBD5E1;
          background: white;
          font-size: 0.875rem;
          outline: none;
        }
        .form-input:focus { border-color: #0A3161; box-shadow: 0 0 0 3px rgba(10,49,97,0.08); }
      `}</style>
    </OfficerLayout>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">
      {label}{required && <span className="text-[#E87722] ml-0.5">*</span>}
    </label>
    <div className="mt-1">{children}</div>
  </div>
);
